import { useState, type FormEvent } from "react";

const FORM_URL = "https://readdy.ai/api/form/dah6ngrp14a1h3mr6ddg";

type Status = "idle" | "submitting" | "success" | "error";

export default function ContactForm() {
  const [status, setStatus] = useState<Status>("idle");
  const [formError, setFormError] = useState("");

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    const honeypot = String(formData.get("contact_alt") ?? "").trim();
    if (honeypot) {
      setStatus("success");
      setFormError("");
      form.reset();
      return;
    }

    const payload = new URLSearchParams();
    formData.forEach((value, key) => {
      if (key === "contact_alt") return;
      const text = String(value).trim();
      if (text) payload.append(key, text);
    });

    setStatus("submitting");
    setFormError("");

    try {
      const response = await fetch(FORM_URL, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString(),
      });
      const responseText = await response.text();
      let parsed: any = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }
      const code = parsed?.code;
      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || "";
      const isSpam = typeof serverMsg === "string" && serverMsg.toLowerCase().includes("spam");

      if (!response.ok || code !== "OK" || isSpam) {
        setStatus("error");
        setFormError(serverMsg || "提交未成功，请稍后重试。");
        return;
      }

      setStatus("success");
      form.reset();
    } catch {
      setStatus("error");
      setFormError("网络异常，请稍后重试。");
    }
  };

  return (
    <form
      id="contact-form"
      data-readdy-form
      method="post"
      action={FORM_URL}
      onSubmit={handleSubmit}
      className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-6"
    >
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div>
          <label htmlFor="contact-name" className="mb-1.5 block text-xs font-medium text-foreground-700">
            怎么称呼你
          </label>
          <input
            id="contact-name"
            name="name"
            type="text"
            required
            placeholder="你的昵称"
            className="w-full rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400"
          />
        </div>
        <div>
          <label htmlFor="contact-email" className="mb-1.5 block text-xs font-medium text-foreground-700">
            邮箱
          </label>
          <input
            id="contact-email"
            name="email"
            type="email"
            required
            placeholder="便于我们回复你"
            className="w-full rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400"
          />
        </div>
      </div>

      <div className="mt-4">
        <label htmlFor="contact-type" className="mb-1.5 block text-xs font-medium text-foreground-700">
          来信类型
        </label>
        <select
          id="contact-type"
          name="type"
          defaultValue="线索投稿"
          className="w-full cursor-pointer rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 outline-none transition-colors focus:border-primary-400"
        >
          <option value="线索投稿">线索投稿 / 补充来源</option>
          <option value="内容纠错">内容纠错</option>
          <option value="提问咨询">提问咨询</option>
          <option value="合作交流">合作交流</option>
        </select>
      </div>

      <div className="mt-4">
        <label htmlFor="contact-message" className="mb-1.5 block text-xs font-medium text-foreground-700">
          内容（500 字以内）
        </label>
        <textarea
          id="contact-message"
          name="message"
          required
          maxLength={500}
          rows={4}
          placeholder="请尽量附上可核验的来源、时间或链接，我们会优先处理。"
          className="w-full resize-none rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm leading-relaxed text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400"
        ></textarea>
      </div>

      <div className="visitor-note-extra" aria-hidden="true">
        <label htmlFor="contact-alt">请勿填写此项</label>
        <input
          id="contact-alt"
          name="contact_alt"
          type="text"
          tabIndex={-1}
          autoComplete="off"
          aria-hidden="true"
          readOnly
        />
      </div>

      <div className="mt-5 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <button
          type="submit"
          disabled={status === "submitting"}
          className="inline-flex cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60"
        >
          <i className={status === "submitting" ? "ri-loader-4-line animate-spin" : "ri-send-plane-line"} aria-hidden="true"></i>
          {status === "submitting" ? "提交中" : "提交"}
        </button>
        <p className="text-xs text-foreground-500">提交后我们会在工作日内回复你提供的邮箱。</p>
      </div>

      {status === "success" && (
        <p
          role="status"
          className="mt-4 flex items-start gap-2 rounded-md border border-secondary-300 bg-secondary-50 px-4 py-3 text-xs leading-relaxed text-secondary-900"
        >
          <i className="ri-checkbox-circle-line mt-0.5 text-base" aria-hidden="true"></i>
          已收到你的来信，感谢补充。若提供了可核验来源，我们会尽快核对并更新到时间线。
        </p>
      )}

      {status === "error" && (
        <p
          role="alert"
          className="mt-4 flex items-start gap-2 rounded-md border border-primary-300 bg-primary-50 px-4 py-3 text-xs leading-relaxed text-primary-900"
        >
          <i className="ri-error-warning-line mt-0.5 text-base" aria-hidden="true"></i>
          {formError || "提交未成功，请稍后重试。"}
        </p>
      )}
    </form>
  );
}